import { defineStore } from 'pinia'
// @ts-ignore
import { ref, computed } from 'vue'
import tenderService from '@/services/tender.service'
import { normalizeTenderResponse, normalizeTenderList } from '@/utils/tender-mapper'
import type {
  TenderResponseDto,
  TenderDocumentResponseDto,
  CreateTenderDto,
  UpdateTenderDto,
  UpdateDocumentDto,
  PageResponseDto,
  TenderQueryParams,
  TenderStatus,
  TenderType
} from '@/types/tender'

/**
 * Pinia store for tender management
 * Handles state, actions, and caching for tenders and tender documents
 */
export const useTenderStore = defineStore('tender', () => {
  // ============================================
  // State
  // ============================================
  
  /**
   * List of tenders (paginated results)
   */
  const tenders = ref<TenderResponseDto[]>([])
  
  /**
   * Currently selected/viewed tender
   */
  const currentTender = ref<TenderResponseDto | null>(null)
  
  /**
   * Documents for the current tender
   */
  const currentTenderDocuments = ref<TenderDocumentResponseDto[]>([])
  
  /**
   * Pagination metadata
   */
  const pagination = ref({
    page: 0,
    size: 20,
    totalElements: 0,
    totalPages: 0,
    last: true,
    first: true
  })
  
  /**
   * Loading states
   */
  const loading = ref(false)
  const loadingDocuments = ref(false)
  const submitting = ref(false)
  
  /**
   * Error state
   */
  const error = ref<string | null>(null)
  
  /**
   * Cache for tenders by ID (for quick lookups)
   */
  const tenderCache = ref<Map<number, TenderResponseDto>>(new Map())

  // ============================================
  // Getters
  // ============================================
  
  /**
   * Get all tenders in current list
   */
  const getTenders = computed(() => tenders.value)
  
  /**
   * Get current tender
   */
  const getCurrentTender = computed(() => currentTender.value)
  
  /**
   * Get current tender documents
   */
  const getCurrentTenderDocuments = computed(() => currentTenderDocuments.value)
  
  /**
   * Check if data is loading
   */
  const isLoading = computed(() => loading.value)
  
  /**
   * Check if documents are loading
   */
  const isLoadingDocuments = computed(() => loadingDocuments.value)
  
  /**
   * Check if submitting data
   */
  const isSubmitting = computed(() => submitting.value)
  
  /**
   * Get current error
   */
  const getError = computed(() => error.value)
  
  /**
   * Get pagination info
   */
  const getPagination = computed(() => pagination.value)
  
  /**
   * Filter tenders by status
   */
  const getTendersByStatus = computed(() => (status: TenderStatus) => {
    return tenders.value.filter((tender: TenderResponseDto) => tender.status === status)
  })
  
  /**
   * Filter tenders by type
   */
  const getTendersByType = computed(() => (type: TenderType) => {
    return tenders.value.filter((tender: TenderResponseDto) => tender.type === type)
  })
  
  /**
   * Get tender from cache by ID
   */
  const getTenderFromCache = computed(() => (id: number) => {
    return tenderCache.value.get(id)
  })

  // ============================================
  // Actions
  // ============================================
  
  /**
   * Fetch tenders with optional filters
   */
  async function fetchTenders(params?: TenderQueryParams) {
    loading.value = true
    error.value = null
    
    try {
      const response = await tenderService.getOpenTenders(params)
      
      if (response) {
        // Normalize backend response to ensure consistent field names
        tenders.value = normalizeTenderList(response.content)
        pagination.value = {
          page: response.page,
          size: response.size,
          totalElements: response.totalElements,
          totalPages: response.totalPages,
          last: response.last,
          first: response.first
        }
        
        // Update cache
        tenders.value.forEach((tender: TenderResponseDto) => {
          tenderCache.value.set(tender.id, tender)
        })
      }
    } catch (err: any) {
      error.value = err.message || 'Failed to fetch tenders'
      throw err
    } finally {
      loading.value = false
    }
  }
  
  /**
   * Fetch tenders created by current user
   */
  async function fetchMyTenders(params?: TenderQueryParams) {
    loading.value = true
    error.value = null
    
    try {
      const response = await tenderService.getMyTenders(params)
      
      if (response) {
        // Normalize backend response to ensure consistent field names
        tenders.value = normalizeTenderList(response.content)
        pagination.value = {
          page: response.page,
          size: response.size,
          totalElements: response.totalElements,
          totalPages: response.totalPages,
          last: response.last,
          first: response.first
        }
        
        // Update cache
        tenders.value.forEach((tender: TenderResponseDto) => {
          tenderCache.value.set(tender.id, tender)
        })
      }
    } catch (err: any) {
      error.value = err.message || 'Failed to fetch my tenders'
      throw err
    } finally {
      loading.value = false
    }
  }
  
  /**
   * Fetch a single tender by ID
   */
  async function fetchTenderById(id: number, useCache: boolean = false) {
    // Check cache first if enabled
    if (useCache && tenderCache.value.has(id)) {
      currentTender.value = tenderCache.value.get(id)!
      return currentTender.value
    }
    
    loading.value = true
    error.value = null
    
    try {
      const tender = await tenderService.getTenderById(id)
      
      if (tender) {
        // Normalize backend response to ensure consistent field names
        const normalizedTender = normalizeTenderResponse(tender)
        currentTender.value = normalizedTender
        tenderCache.value.set(id, normalizedTender)
      }
      
      return currentTender.value
    } catch (err: any) {
      error.value = err.message || 'Failed to fetch tender'
      throw err
    } finally {
      loading.value = false
    }
  }
  
  /**
   * Fetch a tender by reference number
   */
  async function fetchTenderByReference(referenceNumber: string) {
    loading.value = true
    error.value = null
    
    try {
      const tender = await tenderService.getTenderByReference(referenceNumber)
      
      if (tender) {
        // Normalize backend response to ensure consistent field names
        const normalizedTender = normalizeTenderResponse(tender)
        currentTender.value = normalizedTender
        tenderCache.value.set(normalizedTender.id, normalizedTender)
      }
      
      return tender
    } catch (err: any) {
      error.value = err.message || 'Failed to fetch tender'
      throw err
    } finally {
      loading.value = false
    }
  }
  
  /**
   * Create a new tender
   */
  async function createTender(tenderData: CreateTenderDto) {
    submitting.value = true
    error.value = null
    
    try {
      const tender = await tenderService.createTender(tenderData)
      
      if (tender) {
        // Normalize backend response to ensure consistent field names
        const normalizedTender = normalizeTenderResponse(tender)
        // Add to list and cache
        tenders.value.unshift(normalizedTender)
        tenderCache.value.set(normalizedTender.id, normalizedTender)
        currentTender.value = normalizedTender
        return normalizedTender
      }
      
      return tender
    } catch (err: any) {
      error.value = err.message || 'Failed to create tender'
      throw err
    } finally {
      submitting.value = false
    }
  }
  
  /**
   * Update an existing tender
   */
  async function updateTender(id: number, tenderData: UpdateTenderDto) {
    submitting.value = true
    error.value = null
    
    try {
      const updated = await tenderService.updateTender(id, tenderData)
      
      if (updated) {
        // Normalize backend response to ensure consistent field names
        const normalizedTender = normalizeTenderResponse(updated)
        
        // Update in list
        const index = tenders.value.findIndex((t: TenderResponseDto) => t.id === id)
        if (index !== -1) {
          tenders.value[index] = normalizedTender
        }
        
        // Update cache
        tenderCache.value.set(id, normalizedTender)
        
        // Update current if it's the same tender
        if (currentTender.value?.id === id) {
          currentTender.value = normalizedTender
        }
        
        return normalizedTender
      }
      
      return updated
    } catch (err: any) {
      error.value = err.message || 'Failed to update tender'
      throw err
    } finally {
      submitting.value = false
    }
  }
  
  /**
   * Delete a tender
   */
  async function deleteTender(id: number) {
    submitting.value = true
    error.value = null
    
    try {
      await tenderService.deleteTender(id)
      
      // Remove from list
      tenders.value = tenders.value.filter((t: TenderResponseDto) => t.id !== id)
      
      // Remove from cache
      tenderCache.value.delete(id)
      
      // Clear current if it's the deleted tender
      if (currentTender.value?.id === id) {
        currentTender.value = null
      }
    } catch (err: any) {
      error.value = err.message || 'Failed to delete tender'
      throw err
    } finally {
      submitting.value = false
    }
  }
  
  /**
   * Close a tender
   */
  async function closeTender(id: number) {
    submitting.value = true
    error.value = null
    
    try {
      const updated = await tenderService.closeTender(id)
      
      if (updated) {
        // Update in list
        const index = tenders.value.findIndex((t: TenderResponseDto) => t.id === id)
        if (index !== -1) {
          tenders.value[index] = updated
        }
        
        // Update cache
        tenderCache.value.set(id, updated)
        
        // Update current if it's the same tender
        if (currentTender.value?.id === id) {
          currentTender.value = updated
        }
      }
      
      return updated
    } catch (err: any) {
      error.value = err.message || 'Failed to close tender'
      throw err
    } finally {
      submitting.value = false
    }
  }
  
  /**
   * Fetch documents for a tender
   */
  async function fetchTenderDocuments(tenderId: number) {
    loadingDocuments.value = true
    error.value = null
    
    try {
      const documents = await tenderService.getTenderDocuments(tenderId)
      
      if (documents) {
        currentTenderDocuments.value = documents
      }
      
      return documents
    } catch (err: any) {
      error.value = err.message || 'Failed to fetch documents'
      throw err
    } finally {
      loadingDocuments.value = false
    }
  }
  
  /**
   * Upload a document to a tender
   */
  async function uploadDocument(
    tenderId: number,
    file: File,
    documentType: string,
    description?: string,
    tags?: string,
    isPublic: boolean = true
  ) {
    submitting.value = true
    error.value = null
    
    try {
      const document = await tenderService.uploadDocument(
        tenderId,
        file,
        documentType,
        description,
        tags,
        isPublic
      )
      
      if (document) {
        currentTenderDocuments.value.push(document)
      }
      
      return document
    } catch (err: any) {
      error.value = err.message || 'Failed to upload document'
      throw err
    } finally {
      submitting.value = false
    }
  }
  
  /**
   * Upload multiple documents to a tender
   */
  async function uploadTenderDocuments(
    tenderId: number,
    files: File[],
    documentType: string,
    description?: string
  ) {
    submitting.value = true
    error.value = null
    
    try {
      const uploadedDocuments: TenderDocumentResponseDto[] = []
      
      for (const file of files) {
        const document = await tenderService.uploadDocument(
          tenderId,
          file,
          documentType,
          description,
          undefined,
          true
        )
        
        if (document) {
          uploadedDocuments.push(document)
          currentTenderDocuments.value.push(document)
        }
      }
      
      return uploadedDocuments
    } catch (err: any) {
      error.value = err.message || 'Failed to upload documents'
      throw err
    } finally {
      submitting.value = false
    }
  }

  /**
   * Upload multiple documents to a tender in batch (optimized)
   */
  async function uploadDocumentsBatch(
    tenderId: number,
    files: File[],
    documentType: string,
    description?: string,
    tags?: string,
    isPublic: boolean = true
  ) {
    submitting.value = true
    error.value = null
    
    try {
      const documents = await tenderService.uploadDocumentsBatch(
        tenderId,
        files,
        documentType,
        description,
        tags,
        isPublic
      )
      
      if (documents) {
        currentTenderDocuments.value.push(...documents)
      }
      
      return documents
    } catch (err: any) {
      error.value = err.message || 'Failed to upload documents'
      throw err
    } finally {
      submitting.value = false
    }
  }
  
  /**
   * Delete a tender document
   */
  async function deleteDocument(documentId: number) {
    submitting.value = true
    error.value = null
    
    try {
      await tenderService.deleteDocument(documentId)
      
      // Remove from current documents
      currentTenderDocuments.value = currentTenderDocuments.value.filter(
        (doc: TenderDocumentResponseDto) => doc.id !== documentId
      )
    } catch (err: any) {
      error.value = err.message || 'Failed to delete document'
      throw err
    } finally {
      submitting.value = false
    }
  }

  /**
   * Download a tender document
   */
  async function downloadDocument(documentId: number, fileName: string) {
    console.log('[STORE] Download document requested:', { documentId, fileName })
    
    try {
      console.log('[STORE] Calling tenderService.downloadDocument...')
      const blob = await tenderService.downloadDocument(documentId)
      
      console.log('[STORE] Blob received:', {
        type: blob?.type,
        size: blob?.size,
        isBlob: blob instanceof Blob
      })
      
      if (blob) {
        console.log('[STORE] Creating download link...')
        // Create download link
        const url = window.URL.createObjectURL(blob)
        console.log('[STORE] Blob URL created:', url)
        
        const link = document.createElement('a')
        link.href = url
        link.download = fileName
        document.body.appendChild(link)
        
        console.log('[STORE] Triggering download click...')
        link.click()
        
        // Cleanup
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        
        console.log('[STORE] Download completed successfully')
      } else {
        console.error('[STORE] No blob received from service')
        throw new Error('No blob received')
      }
    } catch (err: any) {
      console.error('[STORE] Download failed:', {
        error: err,
        message: err.message,
        stack: err.stack,
        response: err.response
      })
      error.value = err.message || 'Failed to download document'
      throw err
    }
  }
  
  /**
   * Update document metadata
   */
  async function updateDocumentMetadata(documentId: number, updateData: UpdateDocumentDto) {
    submitting.value = true
    error.value = null
    
    try {
      const updated = await tenderService.updateDocument(documentId, updateData)
      
      if (updated) {
        // Update in current documents
        const index = currentTenderDocuments.value.findIndex((doc: TenderDocumentResponseDto) => doc.id === documentId)
        if (index !== -1) {
          currentTenderDocuments.value[index] = updated
        }
      }
      
      return updated
    } catch (err: any) {
      error.value = err.message || 'Failed to update document'
      throw err
    } finally {
      submitting.value = false
    }
  }
  
  /**
   * Set current tender (without fetching)
   */
  function setCurrentTender(tender: TenderResponseDto | null) {
    currentTender.value = tender
    if (tender) {
      tenderCache.value.set(tender.id, tender)
    }
  }
  
  /**
   * Clear current tender
   */
  function clearCurrentTender() {
    currentTender.value = null
    currentTenderDocuments.value = []
  }
  
  /**
   * Clear all tenders
   */
  function clearTenders() {
    tenders.value = []
    pagination.value = {
      page: 0,
      size: 20,
      totalElements: 0,
      totalPages: 0,
      last: true,
      first: true
    }
  }
  
  /**
   * Import tender from TUNEPS platform
   * @param aoNumber - The AO number to import
   * @returns Promise that resolves to the imported tender
   */
  async function importFromTuneps(aoNumber: string): Promise<TenderResponseDto | null> {
    if (!aoNumber || !aoNumber.trim()) {
      throw new Error('AO number is required')
    }

    loading.value = true
    error.value = null

    try {
      const result = await tenderService.importFromTuneps(aoNumber.trim())
      
      if (result) {
        // Normalize the response
        const normalizedTender = normalizeTenderResponse(result)
        
        // Add to cache
        tenderCache.value.set(normalizedTender.id, normalizedTender)
        
        // Add to current list if it's not already there
        if (!tenders.value.find((t: TenderResponseDto) => t.id === normalizedTender.id)) {
          tenders.value.unshift(normalizedTender)
        }
        
        return normalizedTender
      }
      
      return null
    } catch (err: any) {
      console.error('Failed to import tender from TUNEPS:', err)
      error.value = err?.response?.data?.message || err?.message || 'Failed to import tender'
      throw err
    } finally {
      loading.value = false
    }
  }
  
  /**
   * Clear error
   */
  function clearError() {
    error.value = null
  }
  
  /**
   * Clear cache
   */
  function clearCache() {
    tenderCache.value.clear()
  }

  // ============================================
  // Return public API
  // ============================================
  
  return {
    // State
    tenders,
    currentTender,
    currentTenderDocuments,
    pagination,
    loading,
    loadingDocuments,
    submitting,
    error,
    
    // Getters
    getTenders,
    getCurrentTender,
    getCurrentTenderDocuments,
    isLoading,
    isLoadingDocuments,
    isSubmitting,
    getError,
    getPagination,
    getTendersByStatus,
    getTendersByType,
    getTenderFromCache,
    
    // Actions
    fetchTenders,
    fetchMyTenders,
    fetchTenderById,
    fetchTenderByReference,
    createTender,
    updateTender,
    deleteTender,
    closeTender,
    fetchTenderDocuments,
    uploadDocument,
    uploadTenderDocuments,
    uploadDocumentsBatch,
    downloadDocument,
    deleteDocument,
    updateDocumentMetadata,
    importFromTuneps,
    setCurrentTender,
    clearCurrentTender,
    clearTenders,
    clearError,
    clearCache
  }
})
